import {Component, computed, input} from '@angular/core';
import {TableModule} from 'primeng/table';
import {ProgressDashboardContainerComponent} from '../progress-dashboard-container/progress-dashboard-container.component';
import {SubCriteriaAttributeItem} from '../../subcriteria-attribute-evaluation/subcriteria-attribute-evaluation.types';
import {TranslocoPipe} from '@jsverse/transloco';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {EvaluationSubCriteriaImportanceScore} from '@frontend/shared';

export interface TableConfig<TCriteria> {
  /**
   * Function to extract the number identifier from attribute (e.g., ecNumber or scNumber)
   */
  getNumberFromAttribute: (attribute: SubCriteriaAttributeItem) => number | undefined;

  /**
   * Function to find the criteria item by number
   */
  findCriteriaByNumber: (criteria: TCriteria[], number: number) => TCriteria | undefined;

  /**
   * Function to extract translations from criteria
   */
  getTranslations: (criteria: TCriteria) => any;

  /**
   * Function to extract subcriteria number for grouping
   */
  getSubCriteriaNumber?: (attribute: SubCriteriaAttributeItem) => number | undefined;

  /**
   * Sort direction (default is ascending for tables)
   */
  sortAscending?: boolean;
}

@Component({
  selector: 'app-progress-dashboard-generic-table',
  imports: [TableModule, ProgressDashboardContainerComponent, TranslocoPipe, TranslateDirectusPipe],
  templateUrl: './progress-dashboard-generic-table.component.html',
})
export class ProgressDashboardGenericTableComponent<TCriteria> {
  readonly label = input.required<string>();
  readonly subCriteriaAttributes = input.required<SubCriteriaAttributeItem[]>();
  readonly criteria = input.required<TCriteria[]>();
  readonly config = input.required<TableConfig<TCriteria>>();

  private readonly enumOrdinalMap = new Map<EvaluationSubCriteriaImportanceScore, number>([
    [EvaluationSubCriteriaImportanceScore.BASIC_ATTRIBUTE, 0],
    [EvaluationSubCriteriaImportanceScore.PERFORMANCE_ATTRIBUTE, 1],
    [EvaluationSubCriteriaImportanceScore.ENTHUSIASM_ATTRIBUTE, 2],
    [EvaluationSubCriteriaImportanceScore.INDIFFERENT_ATTRIBUTE, 3],
    [EvaluationSubCriteriaImportanceScore.NOT_RELEVANT, 4],
  ]);

  readonly data = computed<any[]>(() => {
    const attributeItems = this.subCriteriaAttributes();
    const criteriaList = this.criteria();
    const tableConfig = this.config();

    const numbers = Array.from(
      new Set(
        attributeItems
          .map((attribute) => tableConfig.getNumberFromAttribute(attribute))
          .filter((num): num is number => num !== undefined),
      ),
    );

    const sortedNumbers = this.sortNumbers(numbers, tableConfig.sortAscending);

    return sortedNumbers.map((number) => {
      const criteriaItem = tableConfig.findCriteriaByNumber(criteriaList, number);
      const criteriaTranslations = criteriaItem ? tableConfig.getTranslations(criteriaItem) : [];

      const filteredAttributes = attributeItems.filter(
        (attribute) => tableConfig.getNumberFromAttribute(attribute) === number,
      );

      const numAttributes = filteredAttributes.length;

      const numSubCriteria = tableConfig.getSubCriteriaNumber
        ? new Set(
            filteredAttributes.map((attribute) => tableConfig.getSubCriteriaNumber!(attribute)),
          ).size
        : 0;

      // NEW: generic completion support (backward compatible)
      const evaluatedAttributes = filteredAttributes.filter((attribute: any) => {
        if (attribute?.isCompleted !== undefined) return Boolean(attribute.isCompleted);
        return attribute?.selectedImportanceScore !== undefined;
      });
      const numEvaluated = new Set(evaluatedAttributes).size;

      // Keep counts-by-score only for attributes that actually have selectedImportanceScore
      const countsByScore = new Map<EvaluationSubCriteriaImportanceScore, number>();
      filteredAttributes
        .filter((attribute: any) => attribute?.selectedImportanceScore !== undefined)
        .forEach((attribute: any) => {
          const score = attribute.selectedImportanceScore as EvaluationSubCriteriaImportanceScore;
          countsByScore.set(score, (countsByScore.get(score) || 0) + 1);
        });

      const allScores = this.getAllScoresFromData(attributeItems);

      const rowData: any = {
        criteria: criteriaTranslations,
        subcriteria: numSubCriteria,
        totalAttributes: numAttributes,
        evaluated: numEvaluated,
        progress: numAttributes > 0 ? Math.round((numEvaluated / numAttributes) * 100) + '%' : '0%',
      };

      allScores.forEach((score) => {
        rowData[score] = countsByScore.get(score) || 0;
      });

      return rowData;
    });
  });

  readonly columns = computed<any[]>(() => {
    const scores = this.getAllScoresFromData(this.subCriteriaAttributes());

    const scoreColumns = scores.map((score) => ({
      field: score,
      headerKey: 'progress.dashboard.criteria.details.table.' + score,
    }));

    return [
      {
        field: 'criteria',
        headerKey: 'progress.dashboard.criteria.details.table.header.criteria',
        directusTranslated: true,
      },
      {
        field: 'subcriteria',
        headerKey: 'progress.dashboard.criteria.details.table.header.subcriteria',
      },
      {
        field: 'totalAttributes',
        headerKey: 'progress.dashboard.criteria.details.table.header.total-attributes',
      },
      {
        field: 'evaluated',
        headerKey: 'progress.dashboard.criteria.details.table.header.evaluated',
      },
      {
        field: 'progress',
        headerKey: 'progress.dashboard.criteria.details.table.header.progress',
      },
      ...scoreColumns,
    ];
  });

  private sortNumbers(numbers: number[], ascending = true): number[] {
    return ascending ? numbers.sort((a, b) => a - b) : numbers.sort((a, b) => b - a);
  }

  private getAllScoresFromData(
    attributes: SubCriteriaAttributeItem[],
  ): EvaluationSubCriteriaImportanceScore[] {
    return Array.from(
      new Set(
        attributes
          .filter((attribute) => attribute?.selectedImportanceScore !== undefined)
          .map(
            (attribute) =>
              attribute.selectedImportanceScore as EvaluationSubCriteriaImportanceScore,
          ),
      ),
    ).sort((a, b) => (this.enumOrdinalMap.get(a) ?? 0) - (this.enumOrdinalMap.get(b) ?? 0));
  }
}
